import { z } from 'zod'
import { ONLY_NUMBERS_REGEX } from '@Const/regexp'
import { IS_NUMBER_MESSAGE, REQUIRED_MESSAGE, STATIC_LENGTH_MESSAGE } from '@Const/error-messages'
import { stringSizeMaxMessage, stringSizeMinMessage } from '@Entities/schemas/work/error-messages'
import { FormFields } from '@App/forms/v2/common/form-field'
import { FormFieldTypes } from '@App/enum/form-field-types'
import populateSchema from '@App/forms/v2/helpers/populate-schema'
import { isPhoneCodeUS } from '@App/common/utils'

const phoneCountryCodeKey = 'phoneCountryCode'
const phoneAreaCodeKey = 'phoneAreaCode'
const phoneNumberKey = 'phoneNumber'

const phoneCountryCodeLabel = 'Country'
const phoneAreaCodeLabel = 'Area Code'
const phoneNumberLabel = 'Phone Number'

const phoneNumberFormKey = 'phoneNumber'

const phoneNumberSchema = z
    .object({
        [phoneCountryCodeKey]: z.union([z.string(), z.number()]).transform(String),
        [phoneAreaCodeKey]: z.union([
            z.literal(''),
            z
                .string()
                .regex(ONLY_NUMBERS_REGEX, IS_NUMBER_MESSAGE)
                .max(10, stringSizeMaxMessage(phoneAreaCodeLabel, 10))
                .optional(),
        ]),
        [phoneNumberKey]: z
            .string({
                error: REQUIRED_MESSAGE,
            })
            .regex(ONLY_NUMBERS_REGEX, IS_NUMBER_MESSAGE)
            .max(40, stringSizeMaxMessage(phoneNumberLabel, 40)),
    })
    .superRefine((phone, ctx) => {
        if (isPhoneCodeUS(phone.phoneCountryCode)) {
            if (!phone.phoneAreaCode) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: REQUIRED_MESSAGE,
                    path: [phoneAreaCodeKey],
                })
            } else if (phone.phoneAreaCode.length !== 3) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `${STATIC_LENGTH_MESSAGE} 3`,
                    path: [phoneAreaCodeKey],
                })
            }
            if (phone.phoneNumber.length !== 7) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `${STATIC_LENGTH_MESSAGE} 7`,
                    path: [phoneNumberKey],
                })
            }
        }
    })

const phoneNumberOptionalSchema = z
    .object({
        [phoneCountryCodeKey]: z.coerce
            .string({
                error: REQUIRED_MESSAGE,
            })
            .min(1, REQUIRED_MESSAGE)
            .optional(),
        [phoneAreaCodeKey]: z.union([
            z.literal('', {
                error: REQUIRED_MESSAGE,
            }),
            z
                .string({ error: REQUIRED_MESSAGE })
                .min(1, stringSizeMinMessage(phoneAreaCodeLabel, 1))
                .regex(ONLY_NUMBERS_REGEX, IS_NUMBER_MESSAGE)
                .max(10, stringSizeMaxMessage(phoneAreaCodeLabel, 10))
                .optional(),
        ]),
        [phoneNumberKey]: z
            .string({
                error: REQUIRED_MESSAGE,
            })
            .min(6, stringSizeMinMessage(phoneNumberLabel, 6))
            .regex(ONLY_NUMBERS_REGEX, IS_NUMBER_MESSAGE)
            .max(40, stringSizeMaxMessage(phoneNumberLabel, 40))
            .optional(),
    })
    .superRefine((phone, ctx) => {
        const { phoneCountryCode, phoneAreaCode, phoneNumber } = phone

        if (phoneCountryCode || phoneAreaCode || phoneNumber) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: REQUIRED_MESSAGE,
                path: [phoneCountryCodeKey, phoneNumberKey],
            })

            if (isPhoneCodeUS(phoneCountryCode)) {
                if (!phoneAreaCode) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: REQUIRED_MESSAGE,
                        path: [phoneAreaCodeKey],
                    })
                } else if (phoneAreaCode.length !== 3) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `${STATIC_LENGTH_MESSAGE} 3`,
                        path: [phoneAreaCodeKey],
                    })
                }
                if (phoneNumber && phoneNumber.length !== 7) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `${STATIC_LENGTH_MESSAGE} 7`,
                        path: [phoneNumberKey],
                    })
                }
            }
        }
    })

type PhoneNumberType = z.infer<typeof phoneNumberSchema>

const createPhoneNumberFormFields = (parentObjectName: string): FormFields<PhoneNumberType> => {
    const phoneNumberFormFieldsNoSchema: FormFields<PhoneNumberType> = {
        [phoneCountryCodeKey]: {
            name: phoneCountryCodeKey,
            parentObjectName: parentObjectName,
            label: phoneCountryCodeLabel,
            type: FormFieldTypes.DROPDOWN,
            options: [],
            className: 'phone-code-form-type border',
            showLabelOnSelect: false,
            isPhoneType: true,
            assistiveTextFunction: (required: boolean): string => (required ? 'Country Required' : ''),
            schema: z.number(),
            isClearable: true,
        },
        [phoneAreaCodeKey]: {
            name: phoneAreaCodeKey,
            parentObjectName: parentObjectName,
            label: phoneAreaCodeLabel,
            type: FormFieldTypes.TEXT,
            assistiveTextFunction: (required: boolean): string => (required ? 'Area Required' : 'Area'),
        },
        [phoneNumberKey]: {
            name: phoneNumberKey,
            parentObjectName: parentObjectName,
            label: phoneNumberLabel,
            assistiveTextFunction: (required: boolean): string => (required ? 'Number Required' : 'Number'),
            type: FormFieldTypes.TEXT,
        },
    }

    return populateSchema(phoneNumberFormFieldsNoSchema, phoneNumberSchema)
}

const phoneNumberFormFieldsNoSchema: FormFields<PhoneNumberType> = createPhoneNumberFormFields(phoneNumberFormKey)

const phoneNumberFormFields: FormFields<PhoneNumberType> = populateSchema(
    phoneNumberFormFieldsNoSchema,
    phoneNumberSchema
)

export {
    phoneNumberFormFields,
    phoneNumberSchema,
    PhoneNumberType,
    phoneNumberFormKey,
    createPhoneNumberFormFields,
    phoneNumberOptionalSchema,
}
